import { inject, Injectable } from "@angular/core";
import { CloseConformanceToGiasCampaignGQL, CloseConformanceToGiasCampaignMutation, ComparatorOperators, ConformanceToGiasField, ConformanceToGiasQueryComplexInput, ConformanceToGiasSimpleQueryInput, ConformanceToGiasUpdate, CreateOrUpdateConformanceToGiasCampaignGQL, CreateOrUpdateConformanceToGiasCampaignMutation, CreateOrUpdateStandardGQL, ExportGiasGQL, ExportGiasQuery, GetConfermanceGiasGQL, GetConfermanceGiasQuery, GetConformanceGiasStandardGQL, GetConformanceToGiasCampaignsGQL, GetConformanceToGiasCampaignsQuery, InitialMassiveEditGiasGQL, InitialMassiveEditGiasMutation, LegalEntity, OperatorInput, PagingInput, UpdateConformanceGiasGQL, UpdateConformanceGiasMutation } from "../../../graphql/generated";
import { map, Observable } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class ConfermanceGiasService {
    private readonly GetConfermanceGiasGQL: GetConfermanceGiasGQL = inject(GetConfermanceGiasGQL);
    private readonly GetConformanceToGiasCampaignsGQL: GetConformanceToGiasCampaignsGQL = inject(GetConformanceToGiasCampaignsGQL);
    private readonly CreateOrUpdateConformanceToGiasCampaignsGQL: CreateOrUpdateConformanceToGiasCampaignGQL = inject(CreateOrUpdateConformanceToGiasCampaignGQL)
    private readonly CloseConformanceToGIASCampaignGQL: CloseConformanceToGiasCampaignGQL = inject(CloseConformanceToGiasCampaignGQL);
    private readonly UpdateConformanceGiasGQL: UpdateConformanceGiasGQL = inject(UpdateConformanceGiasGQL);
    private readonly ExportGiasGQL: ExportGiasGQL = inject(ExportGiasGQL);
    private readonly InitialMassiveEditGiasGQL: InitialMassiveEditGiasGQL = inject(InitialMassiveEditGiasGQL);

    getConfirmanceGias(campaignId: string, parameters: any): Observable<GetConfermanceGiasQuery['getConformanceGIASAttestationsList']> {
        const params = convertParameters(parameters)
        return this.GetConfermanceGiasGQL.fetch({ campaignId, page: params.page, query: params.query }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getConformanceGIASAttestationsList))
    }

    getConfirmancGiasCampaigns(): Observable<GetConformanceToGiasCampaignsQuery['getConformanceToGIASCampaigns']> {
        return this.GetConformanceToGiasCampaignsGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getConformanceToGIASCampaigns))
    }

    createOrUpdateCampaigns(campaignId: string, year: number, legalEntityTrackingIds: string[]): Observable<CreateOrUpdateConformanceToGiasCampaignMutation['createOrUpdateConformanceToGIASCampaign']> {
        return this.CreateOrUpdateConformanceToGiasCampaignsGQL.mutate({ campaignId, year, legalEntityTrackingIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createOrUpdateConformanceToGIASCampaign))
    }

    closeCampaign(id: string): Observable<CloseConformanceToGiasCampaignMutation['closeConformanceToGIASCampaign']> {
        return this.CloseConformanceToGIASCampaignGQL.mutate({ id }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.closeConformanceToGIASCampaign))
    }

    updateConformanceGias(campaignId: string, input: ConformanceToGiasUpdate): Observable<UpdateConformanceGiasMutation['updateConformanceToGIASAttestation']> {
        return this.UpdateConformanceGiasGQL.mutate({ campaignId, input }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateConformanceToGIASAttestation))
    }

    exportGias(campaignId: string, parameters: any, timeZone: string): Observable<ExportGiasQuery['downloadConformanceGIASAttestationsListExcel']> {
        const params = convertParameters(parameters)
        return this.ExportGiasGQL.fetch({ campaignId, query: params.query, timeZone }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.downloadConformanceGIASAttestationsListExcel))
    }

    initialMassiveEdit(campaignId: string, legalEntityTrackingIds: string[], sourceLegalEntity: string): Observable<InitialMassiveEditGiasMutation['massiveEditConformanceToGIASAttestation']> {
        return this.InitialMassiveEditGiasGQL.mutate({ campaignId, legalEntityTrackingIds, sourceLegalEntity }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.massiveEditConformanceToGIASAttestation))
    }
}

export function convertParameters(parameters: any): { query: ConformanceToGiasQueryComplexInput, page: PagingInput } {
    const query: ConformanceToGiasSimpleQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_ICS[field as keyof typeof ENUM_ICS])?.forEach(key => {

                        query.push({
                            field: key as ConformanceToGiasField,
                            filters: {
                                operator: [ConformanceToGiasField.OverallRatingQuantitative].includes(key) ? ComparatorOperators.Eq : ComparatorOperators.Match,
                                value: formattingValues(values, key)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 1000000
        }
    }
}

export const ENUM_ICS = {
    auditControlApproach: [ConformanceToGiasField.AuditControlApproach],
    businessUnit: [ConformanceToGiasField.BusinessUnit],
    chiefAuditor: [ConformanceToGiasField.ChiefAuditor],
    endDate: [ConformanceToGiasField.EndDate],
    legalEntityName: [ConformanceToGiasField.LegalEntityCode],
    trackingId: [ConformanceToGiasField.LegalEntityTrackingId],
    localChiefConfirmation: [ConformanceToGiasField.LocalChiefConfirmation],
    notes: [ConformanceToGiasField.Notes],
    omexNotes: [ConformanceToGiasField.OmexNotes],
    organizationalUnit: [ConformanceToGiasField.OrganizationalUnit],
    overallRatingQualitative: [ConformanceToGiasField.OverallRatingQualitative],
    overallRatingQuantitative: [ConformanceToGiasField.OverallRatingQuantitative],
    signed: [ConformanceToGiasField.Signed],
    startDate: [ConformanceToGiasField.StartDate],
    status: [ConformanceToGiasField.Status],
    excerciseStatus: [ConformanceToGiasField.ExcerciseStatus],
    timestamp: [ConformanceToGiasField.ConfirmationDate]
}

export function formattingValues(values: any[], key: ConformanceToGiasField) {
    let ret: any[] = []
    switch (key) {
        case (ConformanceToGiasField.LegalEntityCode): {
            ret = values.map(v => v?.split(/-(.+)/)[0]?.trim())
            break;
        }
        case (ConformanceToGiasField.ExcerciseStatus): {
            ret = values.map(v => v?.toLowerCase() === 'in progress' ? 'IN_PROGRESS' : v)
            break;
        }
        case (ConformanceToGiasField.LocalChiefConfirmation): {
            ret = values.map(v => v?.toLowerCase() === 'confirmed' ? 'true' : 'false')
            break;
        }
        case (ConformanceToGiasField.OverallRatingQualitative): {
            ret = values.map(v => String(v)?.toUpperCase().replace(' ', '_'))
            break;
        }
        default: {
            ret = values
        }
    }

    ret = ret.map(v => v?.toLowerCase() === 'empty' ? null : v);
    return ret
}